import React, { FC } from 'react'
import _ from 'lodash'
import {
  Button as SemanticButton,
  ButtonProps as SemanticButtonProps,
} from 'semantic-ui-react'
import type { AutomationTarget } from '../../../types'
import { ColorNames } from '../../../constants/colorVars'
import classnames from 'classnames/bind'
import styles from './styles.scss'

const cx = classnames.bind(styles)

export enum ButtonColors {
  Action = ColorNames.Action,
  Info = ColorNames.Info,
  Success = ColorNames.Success,
  Caution = ColorNames.Caution,
  Error = ColorNames.Error,
  Gray = ColorNames.Gray,
  Purple = ColorNames.Purple,
}

export enum StandardButtonColors {
  Action = ButtonColors.Action,
  Success = ButtonColors.Success,
  Caution = ButtonColors.Caution,
  Error = ButtonColors.Error,
  Gray = ButtonColors.Gray,
  Purple = ButtonColors.Purple,
}

export enum AuxiliaryButtonColors {
  Info = ButtonColors.Info,
  Success = ButtonColors.Success,
  Caution = ButtonColors.Caution,
  Error = ButtonColors.Error,
  Gray = ButtonColors.Gray,
}

// Define the prop types for the Button component
export interface ButtonProps
  extends Omit<SemanticButtonProps, 'className'>,
    AutomationTarget {
  role?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'auxiliary'
    // TODO: deprecate and remove these roles
    | 'default'
    | 'brand'
    | 'navigation'
    | 'navigation-light'
  color?: undefined | ButtonColors | ColorNames // TODO: remove ColorNames in favor of ButtonColors (requires refactor of all usages)
  size?: 'small' | 'medium' | 'large' | 'marshmallow'
  tag?: 'button' | 'div' | 'a'
  isStretch?: boolean
  isSelected?: boolean
  collapse?: boolean
  link?: string | null
  className?: string | null
  children?: React.ReactNode
}

// Defaults live on the destructured params of the Button render fn below
// (React 18/19 deprecated FunctionComponent.defaultProps).

const Button: FC<ButtonProps> = ({
  role,
  color,
  size = 'medium',
  tag = 'button',
  isStretch = false,
  isSelected = false,
  collapse = false,
  link = null,
  className = null,
  children = null,
  elmId = null,
  ...rest
}) => {
  let newRole = role || 'secondary'
  let newColor: ColorNames | undefined = color as ColorNames | undefined

  if (!newColor) {
    newColor = ColorNames.Action
  }

  // the following are for backwards compatibility
  // @ts-ignore next
  if (newColor === 'basic') newColor = ColorNames.Gray
  // @ts-ignore next
  if (newRole === 'default') newRole = 'secondary'

  const baseClasses = [
    'button-root',
    `button-role-${newRole}`,
    `button-color-${_.kebabCase(newColor || ColorNames.Gray)}`,
    `button-is-${size}`,
  ]

  if (isStretch) baseClasses.push('button-is-stretch')
  if (isSelected) baseClasses.push('button-is-selected')
  const tagName = link ? 'a' : tag || 'button'

  const newClassName = cx(
    ...baseClasses.map(cls => styles[cls]),
    { collapse },
    className
  )

  // check if newColor is one of the AuxiliaryColors
  if (
    newRole === 'auxiliary' &&
    !Object.values(AuxiliaryButtonColors).includes(
      newColor as unknown as AuxiliaryButtonColors
    )
  ) {
    console.error(
      `The color "${newColor}" is not applicable for auxiliary buttons. Select another color.`
    )
  }

  if (newRole !== 'secondary' && newColor === ColorNames.Glass) {
    console.error(
      `The color "glass" is only applicable for secondary buttons. Select another color or change the role to secondary.`
    )
  }

  return (
    <SemanticButton
      {...rest} // rest comes first, so we overwrite any default props with ours
      className={newClassName}
      as={tagName}
      href={link || undefined}
      data-elm-id={elmId || undefined}
    >
      {children}
    </SemanticButton>
  )
}

interface ButtonStatics {
  Group: typeof SemanticButton.Group
  ButtonColors: typeof ButtonColors
}

const ButtonWithStatics: React.FC<ButtonProps> & ButtonStatics =
  Button as unknown as React.FC<ButtonProps> & ButtonStatics

// Add the Group sub-component
ButtonWithStatics.Group = ({
  wrap,
  ...props
}: { wrap?: boolean } & SemanticButtonProps) => (
  // @ts-ignore next
  <SemanticButton.Group
    className={cx('button-group', { wrap })}
    {...props}
    basic
  />
)

ButtonWithStatics.ButtonColors = ButtonColors

export default ButtonWithStatics
